/**
 * Overlay Config
 *
 * Configure text overlays (Price, Caption, Hashtags, USPs, CTA)
 */

import { useTranslation } from 'react-i18next';
import { Type, Plus, X, Sparkles } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Switch,
  Input,
  Label,
  Button,
  Slider,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/v2/ui';
import type { OverlayConfig as OverlayConfigType } from '@/lib/api/content-studio';

interface OverlayConfigProps {
  config: OverlayConfigType;
  onChange: (config: OverlayConfigType) => void;
}

export function OverlayConfig({ config, onChange }: OverlayConfigProps) {
  const { t } = useTranslation();
  const o = (key: string) => t(`contentStudio.overlay.${key}`);

  const updateConfig = (updates: Partial<OverlayConfigType>) => {
    onChange({ ...config, ...updates });
  };

  const addUspInput = () => {
    const newUsps = [...(config.usp_texts || []), ''];
    updateConfig({ usp_texts: newUsps });
  };

  const removeUspInput = (index: number) => {
    const newUsps = config.usp_texts.filter((_, i) => i !== index);
    updateConfig({ usp_texts: newUsps });
  };

  const updateUspText = (index: number, text: string) => {
    const newUsps = [...config.usp_texts];
    newUsps[index] = text;
    updateConfig({ usp_texts: newUsps });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Type className="h-5 w-5" />
          {o('title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <ToggleRow
          label={o('showPrice')}
          description={o('showPriceDesc')}
          checked={config.show_price}
          onChange={(checked) => updateConfig({ show_price: checked })}
        />
        <ToggleRow
          label={o('showCaption')}
          description={o('showCaptionDesc')}
          checked={config.show_caption}
          onChange={(checked) => updateConfig({ show_caption: checked })}
        />
        <ToggleRow
          label={o('showHashtags')}
          description={o('showHashtagsDesc')}
          checked={config.show_hashtags}
          onChange={(checked) => updateConfig({ show_hashtags: checked })}
        />

        {/* USP Section */}
        <UspSection config={config} o={o} updateConfig={updateConfig}
          addUspInput={addUspInput} removeUspInput={removeUspInput}
          updateUspText={updateUspText}
        />

        {/* CTA Section */}
        <CtaSection config={config} o={o} updateConfig={updateConfig} />
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, description, checked, onChange }: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <Label className="font-medium">{label}</Label>
        <p className="text-sm text-neutral-500">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function UspSection({ config, o, updateConfig, addUspInput, removeUspInput, updateUspText }: {
  config: OverlayConfigType; o: (k: string) => string;
  updateConfig: (u: Partial<OverlayConfigType>) => void;
  addUspInput: () => void; removeUspInput: (i: number) => void;
  updateUspText: (i: number, t: string) => void;
}) {
  return (
    <div className="space-y-3 p-4 border rounded-lg">
      <ToggleRow
        label={o('uspTitle')} description={o('uspDesc')}
        checked={config.show_usp}
        onChange={(checked) => updateConfig({ show_usp: checked })}
      />
      {config.show_usp && (
        <div className="space-y-3 mt-3">
          <Tabs value={config.usp_mode}
            onValueChange={(v) => updateConfig({ usp_mode: v as 'auto' | 'manual' })}>
            <TabsList className="w-full">
              <TabsTrigger value="auto" className="flex-1">
                <Sparkles className="h-4 w-4 mr-2" />
                {o('uspAutomatic')}
              </TabsTrigger>
              <TabsTrigger value="manual" className="flex-1">
                {o('uspManual')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {config.usp_mode === 'auto' && (
            <div className="space-y-2">
              <Slider label={o('uspCount')} min={1} max={4} step={1}
                value={config.usp_count}
                onChange={(e) => updateConfig({ usp_count: Number(e.target.value) })}
                showValue />
              <p className="text-xs text-neutral-500">
                {o(`uspCountHint_${config.usp_count === 1 ? 'one' : 'other'}`).replace('{{count}}', String(config.usp_count))}
              </p>
            </div>
          )}
          {config.usp_mode === 'manual' && (
            <div className="space-y-2">
              {config.usp_texts.map((text, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder={o('uspPlaceholder').replace('{{index}}', String(index + 1))}
                    value={text}
                    onChange={(e) => updateUspText(index, e.target.value)} />
                  <Button variant="ghost" size="sm"
                    onClick={() => removeUspInput(index)} className="shrink-0">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {config.usp_texts.length < 4 && (
                <Button variant="outline" size="sm" onClick={addUspInput} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  {o('uspAdd')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CtaSection({ config, o, updateConfig }: {
  config: OverlayConfigType; o: (k: string) => string;
  updateConfig: (u: Partial<OverlayConfigType>) => void;
}) {
  return (
    <div className="space-y-3 p-4 border rounded-lg">
      <ToggleRow
        label={o('ctaTitle')} description={o('ctaDesc')}
        checked={config.show_cta}
        onChange={(checked) => updateConfig({ show_cta: checked })}
      />
      {config.show_cta && (
        <div className="space-y-3 mt-3">
          <Tabs value={config.cta_mode}
            onValueChange={(v) => updateConfig({ cta_mode: v as 'auto' | 'manual' })}>
            <TabsList className="w-full">
              <TabsTrigger value="auto" className="flex-1">
                <Sparkles className="h-4 w-4 mr-2" />
                {o('ctaAutomatic')}
              </TabsTrigger>
              <TabsTrigger value="manual" className="flex-1">
                {o('ctaManual')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {config.cta_mode === 'manual' && (
            <Input placeholder={o('ctaPlaceholder')} value={config.cta_text}
              onChange={(e) => updateConfig({ cta_text: e.target.value })} />
          )}
          {config.cta_mode === 'auto' && (
            <p className="text-xs text-neutral-500">
              <Sparkles className="h-3 w-3 inline mr-1" />
              {o('ctaAutoHint')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
